import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: unknown[]) {
  return twMerge(clsx(inputs));
}

/**
 * Parses an ISO datetime string and returns a Date object.
 * Handles both UTC timestamps (ending in Z) and local datetime strings.
 * @param dateTimeString - An ISO datetime string (e.g., "2024-05-10T09:00:00Z" or "2024-05-10T09:00:00")
 * @returns A Date object representing the parsed datetime
 */
export function parseLocalDateTime(dateTimeString: string): Date {
  if (!dateTimeString) {
    return new Date();
  }
  return new Date(dateTimeString);
}

/**
 * Extracts date and time parts from a datetime string.
 * @param dateTimeString - An ISO datetime string
 * @returns Object with date and time strings
 */
export function extractDateTimeParts(dateTimeString: string): { date: string; time: string } {
  if (!dateTimeString) {
    return { date: "", time: "" };
  }
  const dateObj = new Date(dateTimeString);
  const date = dateObj.toISOString().split("T")[0];
  const time = dateObj.toTimeString().slice(0, 5);
  return { date, time };
}

/**
 * Formats a date and time into a local datetime string.
 * @param date - Date string in YYYY-MM-DD format
 * @param time - Time string in HH:MM format
 * @returns ISO datetime string
 */
export function formatLocalDateTime(date: string, time: string): string {
  if (!date) return "";
  if (!time) return `${date}T00:00:00`;
  return `${date}T${time}:00`;
}

/*
 * Parses a currency value from a string or number input.
 * Handles various formats like "$1,000", "1000", "1,000.50", etc.
 * Returns null for null, undefined, or empty string inputs.
 * Returns the number as-is if already a number.
 */
export function parseCurrencyValue(
  value: string | number | null | undefined
): number | null {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  if (typeof value === "number") {
    return isNaN(value) ? null : value;
  }

  // Remove currency symbols, commas, and whitespace
  const cleanedValue = value.replace(/[$,\s]/g, "").trim();

  if (cleanedValue === "") {
    return null;
  }

  const parsed = parseFloat(cleanedValue);
  return isNaN(parsed) ? null : parsed;
}

/**
 * Formats a date string or Date object into a compact relative time format.
 * Examples: "2h ago", "3d ago", "1w ago", "2mo ago", "1y ago"
 */
export function formatCompactRelativeTime(dateInput: string | Date): string {
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 0) {
    return 'just now';
  }

  if (diffInSeconds < 60) {
    return 'just now';
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes}m ago`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours}h ago`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return `${diffInDays}d ago`;
  }

  const diffInWeeks = Math.floor(diffInDays / 7);
  if (diffInWeeks < 4) {
    return `${diffInWeeks}w ago`;
  }

  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) {
    return `${diffInMonths}mo ago`;
  }

  const diffInYears = Math.floor(diffInDays / 365);
  return `${diffInYears}y ago`;
}
